/**
 * RenderProgressPanel — the shared UI for the 8-view studio render's
 * progress display. Used inside the PhotoManager modal (post-upload
 * regeneration) AND in the SellerDashboard upload panel (initial render),
 * so both flows get the identical experience:
 *
 *   • 8 tiles (one per canonical view) that flip Queued → Rendering →
 *     Complete → Failed as each render lands.
 *   • Progress bar with real percent + "Generating photo N of 8 — X%".
 *   • Current stage label (Preparing mesh / Rendering views / Finalising).
 *   • ETA in "About Ns remaining" once at least one view has completed.
 *   • Per-tile retry not needed at this layer — the outer surface handles
 *     it via a full regenerate.
 *
 * Props:
 *   job                     the render_jobs mongo doc, or null when idle
 *                           Shape: { status, stage, stage_label, percent,
 *                                    total_views, eta_seconds, views: [
 *                                      { index, view, state,
 *                                        filename, duration_ms, error } ] }
 *   imgBase                 host prefix for /api/media/images/{filename}
 *   compact (bool)          shrink margins for the tighter SellerDashboard
 *                           inline panel (default false)
 */
import { Loader2, Check, AlertCircle, X as XIcon } from "lucide-react";

const STAGE_ORDER = ["queued", "preparing", "rendering", "saving", "complete", "failed"];

// Pretty labels for view names — kept in one place so both the tile
// caption and the "Generating photo 3 of 8" header stay consistent.
const VIEW_LABELS = {
  front: "Front", back: "Back", left: "Left", right: "Right",
  top: "Top", bottom: "Bottom",
  three_quarter_a: "3/4 A", three_quarter_b: "3/4 B",
};

function humanEta(seconds) {
  if (seconds == null) return null;
  if (seconds < 5) return "Almost done";
  if (seconds < 60) return `About ${seconds}s remaining`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `About ${m}m ${s}s remaining`;
}

export default function RenderProgressPanel({ job, imgBase = "", compact = false,
                                              onCancel = null, cancelling = false }) {
  if (!job) return null;
  const views = job.views || [];
  const total = job.total_views || views.length || 8;
  const percent = Math.max(0, Math.min(100, job.percent ?? 0));
  const stageLabel = job.stage_label || "Working…";
  // "Generating photo N of M — X%": pick the tile that is currently rendering.
  // Fall back to the last completed one so the copy never goes blank between views.
  const activeIdx = views.findIndex((v) => v.state === "rendering");
  const doneCount = views.filter((v) => v.state === "complete").length;
  const currentIdx = activeIdx >= 0 ? activeIdx : Math.min(doneCount, total - 1);
  const currentView = views[currentIdx] || {};
  const currentLabel = VIEW_LABELS[currentView.view] || currentView.view || `View ${currentIdx + 1}`;

  const failed = job.status === "failed";
  const complete = job.status === "complete";
  const running = job.status === "queued" || job.status === "running";
  const cancelled = failed && job.kind === "cancelled";
  const eta = humanEta(job.eta_seconds);

  return (
    <div
      className={`rounded-lg border ${failed ? "border-destructive/30 bg-destructive/5" : "border-primary/20 bg-primary/5"} ${compact ? "p-3" : "p-4"}`}
      data-testid="render-progress-panel"
    >
      {/* --- header row --- */}
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          {failed ? (
            <AlertCircle className={`h-4 w-4 flex-shrink-0 ${cancelled ? "text-amber-600" : "text-destructive"}`} />
          ) : complete ? (
            <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
          ) : (
            <Loader2 className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
          )}
          <div className="min-w-0">
            <p
              className={`text-[12px] font-semibold truncate ${cancelled ? "text-amber-700" : failed ? "text-destructive" : "text-primary"}`}
              data-testid="render-progress-stage"
            >
              {cancelled
                ? "Render cancelled"
                : failed
                  ? "Rendering failed"
                  : complete
                    ? "All studio photos are ready"
                    : `Generating photo ${Math.min(currentIdx + 1, total)} of ${total} — ${currentLabel}`}
            </p>
            <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider">
              {stageLabel}
              {eta && !failed && !complete ? <> · {eta}</> : null}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span
            className={`text-[13px] font-mono font-bold ${cancelled ? "text-amber-700" : failed ? "text-destructive" : "text-primary"}`}
            data-testid="render-progress-percent"
          >
            {percent}%
          </span>
          {/* Cancel button — visible ONLY while the job is running. Frees
              the render semaphore slot the moment the subprocess exits,
              so the next queued upload starts immediately. */}
          {running && onCancel ? (
            <button
              type="button"
              onClick={onCancel}
              disabled={cancelling}
              data-testid="render-progress-cancel"
              title="Stop this render and free the slot for the next upload"
              className="inline-flex items-center gap-1 border border-destructive/50 text-destructive rounded-md px-2 py-1 text-[11px] font-semibold hover:bg-destructive/5 disabled:opacity-60"
            >
              {cancelling ? <Loader2 className="h-3 w-3 animate-spin" /> : <XIcon className="h-3 w-3" />}
              {cancelling ? "Stopping…" : "Cancel"}
            </button>
          ) : null}
        </div>
      </div>

      {/* --- progress bar --- */}
      <div className="h-2 rounded-full bg-slate-200/70 overflow-hidden mb-3">
        <div
          className={`h-full transition-all ${failed ? "bg-destructive" : "bg-primary"}`}
          style={{ width: `${percent}%` }}
          data-testid="render-progress-bar"
        />
      </div>

      {/* --- tile grid --- */}
      <div className="grid grid-cols-4 gap-2">
        {Array.from({ length: total }).map((_, i) => {
          const v = views[i] || { state: "queued", view: `angle_${i}` };
          const label = VIEW_LABELS[v.view] || `View ${i + 1}`;
          const isDone = v.state === "complete";
          const isRendering = v.state === "rendering";
          const isFailed = v.state === "failed";
          return (
            <div
              key={i}
              data-testid={`render-tile-${i}`}
              data-tile-state={v.state}
              className={`relative rounded-md overflow-hidden aspect-square border transition-all ${
                isDone
                  ? "border-emerald-500/40 bg-white"
                  : isRendering
                    ? "border-primary/50 bg-primary/5 shadow-inner"
                    : isFailed
                      ? "border-destructive/40 bg-destructive/5"
                      : "border-slate-200 bg-slate-50"
              }`}
            >
              {/* thumbnail when done */}
              {isDone && v.filename ? (
                <img
                  src={`${imgBase}/api/media/images/${v.filename}?w=200`}
                  alt={label}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  {isRendering ? (
                    <Loader2 className="h-5 w-5 text-primary animate-spin" />
                  ) : isFailed ? (
                    <AlertCircle className="h-5 w-5 text-destructive" />
                  ) : (
                    <span className="text-[9px] font-mono uppercase tracking-widest text-slate-400">
                      Queued
                    </span>
                  )}
                </div>
              )}
              {/* corner caption */}
              <div className={`absolute bottom-0 inset-x-0 text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 truncate ${
                isDone ? "bg-emerald-600/85 text-white"
                       : isRendering ? "bg-primary/85 text-white"
                                     : isFailed ? "bg-destructive/85 text-white"
                                                : "bg-white/85 text-slate-500"
              }`}>
                {label}
              </div>
            </div>
          );
        })}
      </div>

      {/* --- error footer --- */}
      {failed && job.error ? (
        <p className="mt-3 text-[12px] text-destructive font-medium" data-testid="render-progress-error">
          {job.error}
        </p>
      ) : null}
    </div>
  );
}
